import { eff } from "@/core/utils/effect-ts-abbr";
import { SmoothScrollTo } from "@public/js/components/smothScrollTo.js";
import { Directive } from "vue";

type AppScrollSpyLinkElement = HTMLLinkElement & {
    _appScrollSpySmoothScrollTo?: SmoothScrollTo;
};

type AppScrollSpyElement = HTMLLinkElement & {
    _appScrollSpyDispose?: () => void;
    _appScrollSpyCalcPositions?: () => void;
};

/**
 * Integrates static/src/js/components/scrollSpy.js
 *
 * This directive can be used instead of "js-scrollSection" class that used in the "static".
 */
export const appScrollSpyDirective: Directive<AppScrollSpyElement> = {
    mounted(el) {
        const calcPositions = () => {
            const links = el.querySelectorAll(
                'a[href*="#"]:not([href="#"])',
            ) as NodeListOf<AppScrollSpyLinkElement>;

            eff.fn.pipe(
                [...links],
                eff.array.filter(
                    (link) => "_appScrollSpySmoothScrollTo" in link === false,
                ),
                eff.array.map((link) =>
                    eff.t.succeedWith(() => {
                        link._appScrollSpySmoothScrollTo = new SmoothScrollTo(link);
                    }),
                ),
                eff.t.collectAllPar,
                eff.t.run,
            );

            const sections = eff.fn.pipe(
                [...links],
                eff.array.map((link) => {
                    const id = (link.getAttribute("href") || "").slice(1);
                    const elementToScroll = document.getElementById(id);

                    const sectionTop =
                        (elementToScroll?.getBoundingClientRect().top || 0) - 140;

                    return [id, sectionTop] as const;
                }),
            );

            const linksBySection = eff.fn.pipe(
                [...links],
                eff.array.reduce({} as Record<string, HTMLLinkElement>, (acc, link) => {
                    const id = (link.getAttribute("href") || "").slice(1);

                    acc[id] = link;

                    return acc;
                }),
            );

            const activeSection = eff.fn.pipe(
                sections,
                eff.array.reduce(
                    sections.length > 0 ? sections[0] : "",
                    (acc, [section, sectionTop]) => {
                        if (sectionTop <= 0) {
                            return section;
                        }

                        return acc;
                    },
                ),
            );

            eff.fn.pipe(
                sections,
                eff.array.map(([section]) =>
                    eff.t.succeedWith(() => {
                        if (section in linksBySection) {
                            if (section === activeSection) {
                                linksBySection[section].classList.add("is-active");
                            } else {
                                linksBySection[section].classList.remove("is-active");
                            }
                        }
                    }),
                ),
                eff.t.collectAllPar,
                eff.t.run,
            );
        };

        calcPositions();

        window.addEventListener("scroll", calcPositions);

        el._appScrollSpyDispose = () => {
            window.removeEventListener("scroll", calcPositions);
            const links = el.querySelectorAll(
                'a[href*="#"]:not([href="#"])',
            ) as NodeListOf<AppScrollSpyLinkElement>;

            eff.fn.pipe(
                [...links],
                eff.array.map((link) =>
                    eff.t.succeedWith(() => {
                        link._appScrollSpySmoothScrollTo?.destroy(link);
                    }),
                ),
                eff.t.collectAllPar,
                eff.t.run,
            );
        };
        el._appScrollSpyCalcPositions = calcPositions;
    },
    unmounted(el) {
        if (el._appScrollSpyDispose) {
            el._appScrollSpyDispose();
        }
    },
    updated(el) {
        if (el._appScrollSpyCalcPositions) {
            el._appScrollSpyCalcPositions();
        }
    },
};
